import React from "react";
import { CalendarPlus, Loader2 } from "lucide-react";
import HolidayChoices from "./HolidayChoices";

const PreviewText = ({ preview, totalToCreate }) => {
  if (!preview) return <p className="text-neutral-400">Aperçu…</p>;
  const already = preview.contracts.reduce((n, c) => n + (c.already || 0), 0);
  const parts = [`${totalToCreate} nouvelle(s) intervention(s) à créer`];
  if (already) parts.push(`${already} déjà planifiée(s)`);
  if (preview.holidays_count) parts.push(`${preview.holidays_count} férié(s)`);
  return (
    <>
      <p className="font-semibold">{parts.join(" · ")}</p>
      <p className="text-xs text-neutral-500">{preview.contracts.map((c) => `${c.client_nom} : ${c.dates.length}`).join(" · ") || "Aucun contrat actif"}</p>
    </>
  );
};

const AutoPlanSettings = ({ auto, setAuto, toggleAuto, isMaster }) => (
  <div className="md:col-span-3 flex flex-wrap items-center gap-4 border-t border-neutral-100 pt-3 text-sm text-neutral-700">
    <label className="flex items-center gap-2">
      <input type="checkbox" className="accent-brand w-4 h-4" checked={!!auto.enabled} disabled={!isMaster} data-testid="plan-auto-enabled" onChange={(e) => toggleAuto({ enabled: e.target.checked })} />
      Générer automatiquement le mois suivant
    </label>
    <label className="flex items-center gap-2">le <input type="number" min={1} max={28} className="w-16 border border-neutral-300 rounded px-2 py-1 text-sm" value={auto.day_of_month} disabled={!isMaster} onChange={(e) => setAuto({ ...auto, day_of_month: e.target.value })} onBlur={() => toggleAuto({ day_of_month: parseInt(auto.day_of_month, 10) || 25 })} /> de chaque mois à 6h30</label>
    {auto.last_run && <span className="text-xs text-neutral-400">Dernière génération : {new Date(auto.last_run).toLocaleString("fr-FR")} ({auto.last_month}, {auto.last_created} créée(s))</span>}
  </div>
);

/** Bloc « Générer le planning du mois » : mois, aperçu, fériés, génération et réglage automatique. */
const PlanGenerator = ({ month, setMonth, preview, reports, setReports, auto, setAuto, toggleAuto, generate, isMaster }) => {
  const reported = Object.values(reports).filter((v) => v !== "skip").length;
  const totalToCreate = (preview?.total_new || 0) + reported;
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4 mb-5 grid md:grid-cols-[auto_1fr_auto] gap-4 items-center" data-testid="plan-generator">
      <div>
        <label className="block text-xs font-semibold text-neutral-600 mb-1">Mois à planifier</label>
        <input type="month" className="border border-neutral-300 rounded-md px-3 py-2 text-sm" value={month} data-testid="plan-month" onChange={(e) => setMonth(e.target.value)} />
      </div>
      <div className="text-sm text-neutral-700" data-testid="plan-preview">
        <PreviewText preview={preview} totalToCreate={totalToCreate} />
        {auto?.running && <p className="text-xs text-brand font-semibold mt-1 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Génération en cours {auto.progress}/{auto.total}…</p>}
      </div>
      <button onClick={generate} disabled={auto?.running || !totalToCreate} data-testid="plan-generate-btn" className="flex items-center gap-2 bg-neutral-900 hover:bg-neutral-800 text-white text-sm font-semibold px-4 py-2.5 rounded-md disabled:opacity-50">
        <CalendarPlus size={16} /> Générer le planning
      </button>
      <HolidayChoices preview={preview} reports={reports} onChange={(k, v) => setReports((r) => ({ ...r, [k]: v }))} />
      {auto && <AutoPlanSettings auto={auto} setAuto={setAuto} toggleAuto={toggleAuto} isMaster={isMaster} />}
    </div>
  );
};

export default PlanGenerator;
